import React, { createContext, useContext, useState, useEffect, useReducer } from "react";
import { useLocalstorageState } from "rooks";
import AdBlockReducer, { initialState } from "./AdBlockReducer";
import { getCircularMeta } from "api";

export const AdBlocksContext = createContext();
export const useAdBlocksContext = () => useContext(AdBlocksContext);

export const AdBlocksProvider = ({ children }) => {
    const STORAGE_KEY = "addison-adblocks-state";
    
    // Use reducer for state management
    const [state, dispatch] = useReducer(AdBlockReducer, initialState);
    
    // Use localStorage state for persistence
    const [value, set] = useLocalstorageState(STORAGE_KEY, initialState);
    
    // Effect to synchronize state with local storage
    useEffect(() => {
        set(state);
    }, [state, set]);
    // Function to update the ad block data
    const updateAdBlockData = (data) => {
        dispatch({ type: "UPDATE_ADBLOCK_DATA", payload: data });
    }
    // Function to reset the ad block state
    const resetAdBlockState = () => {
        dispatch({ type: "RESET_ADBLOCK_STATE" });
    }
    // Function to set loading state
    const setLoading = (isLoading) => {
        dispatch({ type: "SET_LOADING", payload: isLoading });
    }
    // Function to set error state
    const setError = (error) => {
        dispatch({ type: "SET_ERROR", payload: error });
    }
    // Function to set image data
    const setImageData = (data) => {
        dispatch({ type: "SET_IMAGE_DATA", payload: data });
    }
    // Function to set ad block coordinates
    const setAdBlockCoords = (coords) => {
        dispatch({ type: "SET_ADBLOCK_COORDS", payload: coords });
    }
    // Function to set section coordinates
    const setSectionCoords = (coords) => {  
        dispatch({ type: "SET_SECTION_COORDS", payload: coords });
    }
    // Function to set page dimensions
    const setPageDimensions = (width, height) => {
        dispatch({ type: "SET_PAGE_DIMENSIONS", payload: { width, height } });
    }
    // Function to set ad block filename
    const setAdBlockFilename = (filename) => {
        dispatch({ type: "SET_ADBLOCK_FILENAME", payload: filename });
    }
    // Function to set page image
    const setPageImage = (image) => {
        dispatch({ type: "SET_PAGE_IMAGE", payload: image });
    }
    // Function to set page list
    const setPageList = (pages) => {
        dispatch({ type: "SET_PAGE_LIST", payload: pages });
    }
    // Function to set ad block list
    const setAdBlockList = (adBlocks) => {
        dispatch({ type: "SET_ADBLOCK_LIST", payload: adBlocks });
    }
    // Function to set current ad block data
    const setCurrentAdBlockData = (data) => {
        dispatch({ type: "SET_CURRENT_ADBLOCK_DATA", payload: data });
    }
    // Function to set circular metadata
    const setCircularMetadata = (metadata) => {
        dispatch({ type: "SET_CIRCULAR_METADATA", payload: metadata });
    }
    // Function to set ad block metadata
    const setAdBlockMetadata = (metadata) => {
        dispatch({ type: "SET_ADBLOCK_METADATA", payload: metadata });
    }
    // Function to set ad block status
    const setAdBlockStatus = (status) => {
        dispatch({ type: "SET_ADBLOCK_STATUS", payload: status });
    }
    // Function to set ad block events
    const setAdBlockEvents = (events) => {
        dispatch({ type: "SET_ADBLOCK_EVENTS", payload: events });
    }
    // Function to set ad block info
    const setAdBlockInfo = (info) => {
        dispatch({ type: "SET_ADBLOCK_INFO", payload: info });
    }
    // Function to set ad block start time
    const setAdBlockStartTime = (time) => {
        dispatch({ type: "SET_ADBLOCK_START_TIME", payload: time });
    }
    // Function to set ad block import status
    const setAdBlockImportStatus = (status) => {
        dispatch({ type: "SET_ADBLOCK_IMPORT_STATUS", payload: status });
    }
    // Function to set ad block new item flag
    const setAdBlockNewItemFlag = (flag) => {
        dispatch({ type: "SET_ADBLOCK_NEW_ITEM_FLAG", payload: flag });
    }
    // Function to set ad block select varieties flag
    const setAdBlockSelectVarietiesFlag = (flag) => {
        dispatch({ type: "SET_ADBLOCK_SELECT_VARIETIES_FLAG", payload: flag });
    }
    // Function to set ad block bulk quantity flag
    const setAdBlockBulkQtyFlag = (flag) => {
        dispatch({ type: "SET_ADBLOCK_BULK_QTY_FLAG", payload: flag });
    }
    // Function to set ad block anomaly flag
    const setAdBlockAnomalyFlag = (flag) => {
        dispatch({ type: "SET_ADBLOCK_ANOMALY_FLAG", payload: flag });
    }
    // Function to set ad block split flag
    const setAdBlockSplitFlag = (flag) => {
        dispatch({ type: "SET_ADBLOCK_SPLIT_FLAG", payload: flag });
    }
    // Function to set product data
    const setProductData = (data) => {
        dispatch({ type: "SET_PRODUCT_DATA", payload: data });
    }
    // Function to set offer data
    const setOfferData = (data) => {
        dispatch({ type: "SET_OFFER_DATA", payload: data });
    }
    // Function to set circular name
    const setCircularName = (name) => {
        dispatch({ type: "SET_CIRCULAR_NAME", payload: name });
    }
    // Function to set circular type
    const setCircularType = (type) => {
        dispatch({ type: "SET_CIRCULAR_TYPE", payload: type });
    }
    // Function to set retailer ID
    const setRetailerID = (id) => {
        dispatch({ type: "SET_RETAILER_ID", payload: id });
    }
    // Function to set page type
    const setPageType = (type) => {
        dispatch({ type: "SET_PAGE_TYPE", payload: type });
    }
    // Function to reset page state
    const resetPageState = () => {
        dispatch({ type: "RESET_PAGE_STATE" });
    }   
    // Function to set ad block data products
    const setAdBlockDataProducts = (products) => {
        dispatch({ type: "SET_ADBLOCK_DATA_PRODUCTS", payload: products });
    }
    // Function to set ad block data offers
    const setAdBlockDataOffers = (offers) => {
        dispatch({ type: "SET_ADBLOCK_DATA_OFFERS", payload: offers });
    }
    // Function to set active tab
    const setActiveTab = (tab) => {
        dispatch({ type: "SET_ACTIVE_TAB", payload: tab });
    }
    // Function to set approval status
    const setApprovalStatus = (status) => {
        dispatch({ type: "SET_APPROVAL_STATUS", payload: status });
    }

    const getCircularMetadata = async (id) => {
        setLoading(true);
        try {
        const res = await getCircularMeta(id);
        if (res.success) {
            dispatch({ type: "GET_CIRCULAR_METADATA", payload: res.payload });
        } else {
            dispatch({ type: "SET_ERROR", payload: res.error });
        }
    } catch (error) {
        dispatch({ type: "SET_ERROR", payload: error.message });
    } finally {
        setLoading(false);
    }
    }
    // Additional helper functions
    const initAdBlocks = () => {
        dispatch({ type: "INIT_ADBLOCKS" });
    }
    
    const getAdBlocks = (data) => {
        dispatch({ type: "GET_ADBLOCKS", payload: data });
    }
    
    const getMoreAdBlocks = (data) => {
        dispatch({ type: "GET_MORE_ADBLOCKS", payload: data });
    }
    
    const setSubmitting = (isSubmitting) => {
        dispatch({ type: "SET_SUBMITTING", payload: isSubmitting });
    }
    
    const setFetching = (isFetching) => {
        dispatch({ type: "SET_FETCHING", payload: isFetching });
    }
    
    const setSuggestionLoading = (isSuggestionLoading) => {
        dispatch({ type: "SET_SUGGESTION_LOADING", payload: isSuggestionLoading });
    }
    
    // Function to reset page
    const resetPage = () => {
        dispatch({ type: "RESET_PAGE" });
    }
    // Return context value
    return (
        <AdBlocksContext.Provider value={{
            state,
            
            // Core state management
            setLoading,
            setSubmitting,
            setFetching,
            setSuggestionLoading,
            setError,
            
            // AdBlock data management
            initAdBlocks,
            getAdBlocks,
            getMoreAdBlocks,
            updateAdBlockData,
            resetAdBlockState,
            
            // Image and coordinates
            setImageData,
            setAdBlockCoords,
            setSectionCoords,
            setPageDimensions,
            setAdBlockFilename,
            setPageImage,
            
            // Lists and current data
            setPageList,
            setAdBlockList,
            setCurrentAdBlockData,
            
            // Metadata
            getCircularMetadata,
            setCircularMetadata,
            setAdBlockMetadata,
            setAdBlockStatus,
            setAdBlockEvents,
            setAdBlockInfo,
            setAdBlockStartTime,
            setAdBlockImportStatus,
            
            // Flags
            setAdBlockNewItemFlag,
            setAdBlockSelectVarietiesFlag,
            setAdBlockBulkQtyFlag,
            setAdBlockAnomalyFlag,
            setAdBlockSplitFlag,
            
            // Product and offer data
            setProductData,
            setOfferData,
            setAdBlockDataProducts,
            setAdBlockDataOffers,
            
            // Circular info
            setCircularName,
            setCircularType,
            setRetailerID,
            setPageType,
            
            // Page management
            resetPageState,
            resetPage,
            
            // UI state
            setActiveTab,
            setApprovalStatus,
        }}>
            {children}
        </AdBlocksContext.Provider>
    );
};
